import { useState } from 'react';
import { Voice } from '@/types/voice/voice';
import { cn } from "@/lib/utils";
import VoiceCard from "@/pages/UserHub/VoicePlayground/components/VoiceCard";

interface VoiceGridProps {
  voices: Voice[];
  selectedVoiceId: string;
  playingVoiceId: string | null;
  favoriteVoiceIds: Set<string>;
  onVoiceSelect: (voiceId: string) => void;
  onVoicePick?: (voice: Voice) => void;
  onPlay: (voice: Voice) => void;
  onToggleFavorite: (voice: Voice, e?: React.MouseEvent, fromAllVoicesTab?: boolean) => void;
  onTryText: (voice: Voice) => void;
  onAddVoice?: (voice: Voice) => Promise<Voice | null>;
  fromAllVoicesTab?: boolean;
  allowLibraryAdd?: boolean;
}

export const VoiceGrid = ({
  voices,
  selectedVoiceId,
  playingVoiceId,
  favoriteVoiceIds,
  onVoiceSelect,
  onVoicePick,
  onPlay,
  onToggleFavorite,
  onTryText,
  onAddVoice,
  fromAllVoicesTab = false,
  allowLibraryAdd = true
}: VoiceGridProps) => {
  const [addingVoiceId, setAddingVoiceId] = useState<string | null>(null);

  const handleVoiceSelectClick = async (voice: Voice) => {
    if (fromAllVoicesTab && onAddVoice && allowLibraryAdd) {
      setAddingVoiceId(voice.id);
      try {
        const addedVoice = await onAddVoice(voice);
        const selectedVoice = addedVoice ?? voice;
        onVoicePick?.(selectedVoice);
        onVoiceSelect(selectedVoice.id);
      } finally {
        setAddingVoiceId(null);
      }
      return;
    }

    onVoicePick?.(voice);
    onVoiceSelect(voice.id);
  };

  const handleCardPlay = (voice: Voice, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (addingVoiceId !== voice.id) {
      onPlay(voice);
    }
  };

  const handleCardTryText = (voice: Voice, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (addingVoiceId !== voice.id) {
      onTryText(voice);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-4">
      {voices.map((voice) => (
        <div
          key={voice.id}
          className={cn(
            voice.id === selectedVoiceId && 'ring-2 ring-blue-500 rounded-lg',
            addingVoiceId === voice.id && 'opacity-60 pointer-events-none'
          )}
          onClick={() => handleVoiceSelectClick(voice)}
        >
          <VoiceCard
            voice={voice}
            viewMode="compact"
            isPlaying={playingVoiceId === voice.id}
            isFavorited={favoriteVoiceIds.has(voice.id)}
            onPlay={(v) => handleCardPlay(v)}
            onToggleFavorite={(v, e) => {
              if (addingVoiceId !== v.id) {
                onToggleFavorite(v, e, fromAllVoicesTab);
              }
            }}
            onTryText={(v) => handleCardTryText(v)}
          />
        </div>
      ))}
    </div>
  );
};
